/**
 * Who spoke when — the whole activity timeline, computed from the turns already on screen.
 *
 * **Nothing is persisted and nothing is fetched.** Every turn the viewer has loaded already
 * carries its speaker and its start/end seconds, so a per-speaker track is arithmetic over the
 * list rather than a second read model that could disagree with the transcript beside it. That is
 * also why this is a pure function in `lib/` and not a hook: what it returns is a picture of the
 * turns it was handed, and the dialog re-derives it as pages land.
 *
 * **A turn with no timestamps is not on any track.** The vendor labels some turns with a speaker
 * and no offsets, and a segment has to sit *somewhere* in time to mean anything — placing an
 * untimed turn at zero, or spreading it evenly, would draw a claim about the recording that
 * nothing measured. Those turns are counted nowhere and the panel says nothing about them; a
 * recording where *no* turn carries a time has no timeline at all, which the dialog reads as
 * "do not offer the control".
 */

import { formatDuration } from "@/lib/formatters";
import type { TranscriptTurn } from "./transcript-turns-view";

/** One stretch of one voice, positioned as a fraction of the whole recording. */
export type SpeakerActivitySegment = {
  /** The turn this segment draws, so a click can scroll to it. */
  turnKey: number;
  startSeconds: number;
  endSeconds: number;
  /** `0`–`1` across the track. */
  startRatio: number;
  widthRatio: number;
};

/** One voice's whole meeting. */
export type SpeakerActivityRow = {
  /** The vendor's speaker number, or `null` for a timed recording it told nobody apart in. */
  speaker: number | null;
  segments: SpeakerActivitySegment[];
  /** Seconds this voice held the floor, summed over its segments. */
  talkSeconds: number;
};

/** One label under the shared axis. */
export type SpeakerActivityTick = {
  seconds: number;
  label: string;
  /** `0`–`1` across the track, so the last tick sits at the end rather than at a round number. */
  ratio: number;
};

export type SpeakerActivity = {
  rows: SpeakerActivityRow[];
  /** The span the tracks are scaled against. */
  durationSeconds: number;
  ticks: SpeakerActivityTick[];
};

/**
 * A segment narrower than this is drawn at this width instead.
 *
 * A two-second interjection in a two-hour meeting is a fraction of a pixel, and a track that
 * silently drops it reads as a voice that never spoke there. The design draws those as a hairline;
 * the widening is cosmetic and never changes `talkSeconds`, which stays the measured total.
 */
const MIN_SEGMENT_RATIO = 0.0015;

/**
 * The tick steps a shared axis may use, in seconds.
 *
 * A closed ladder rather than a computed "nice number": every step here is a duration a person
 * reads a recording in (fifteen seconds, half a minute, a minute, five, …), and the axis picks the
 * smallest one that keeps the labels from crowding.
 */
const AXIS_STEPS_SECONDS = [
  15, 30, 60, 120, 300, 600, 900, 1_800, 3_600, 7_200, 10_800, 21_600,
] as const;

/** Most interior ticks the axis draws, before the closing total. */
const MAX_AXIS_TICKS = 4;

/**
 * The timeline for a set of turns, or `null` when there is nothing to draw one from.
 *
 * `null` means one of exactly three things, and the dialog treats them the same way — no turn has
 * a timestamp, the recording has no measurable length, or fewer than two voices were told apart —
 * because in each the control would open a panel that shows nothing. The last is the point of the
 * panel: it exists to show who spoke *against whom* and for how long; one voice talking for the
 * whole recording is a fact the transcript already states.
 *
 * `durationSeconds` is the recording's own length where the row knows it, so a meeting that ends
 * in silence still shows that silence. Where it is absent or shorter than the turns themselves
 * (an inconsistency the viewer must survive rather than argue with), the last turn's end is the
 * span.
 */
export function buildSpeakerActivity(
  turns: readonly TranscriptTurn[],
  durationSeconds?: number | null
): SpeakerActivity | null {
  const timed = turns.filter(
    (turn): turn is TranscriptTurn & { header: { startSeconds: number } } =>
      typeof turn.header?.startSeconds === "number" && Number.isFinite(turn.header.startSeconds)
  );
  if (timed.length === 0) return null;

  const lastEnd = timed.reduce((latest, turn) => {
    const end = turn.header.endSeconds ?? turn.header.startSeconds;
    return Math.max(latest, end, turn.header.startSeconds);
  }, 0);
  const measured =
    typeof durationSeconds === "number" && Number.isFinite(durationSeconds) ? durationSeconds : 0;
  const span = Math.max(measured, lastEnd);
  if (span <= 0) return null;

  // A `Map` keyed by the speaker number, built in the order the voices first speak, so the rows
  // read down the panel in the order somebody hears them rather than by number.
  const bySpeaker = new Map<number | null, SpeakerActivityRow>();
  for (const turn of timed) {
    const speaker = turn.header.speaker ?? null;
    const row = bySpeaker.get(speaker) ?? { speaker, segments: [], talkSeconds: 0 };
    const startSeconds = Math.max(0, Math.min(turn.header.startSeconds, span));
    const endSeconds = Math.max(
      startSeconds,
      Math.min(turn.header.endSeconds ?? startSeconds, span)
    );
    const startRatio = startSeconds / span;
    row.segments.push({
      turnKey: turn.key,
      startSeconds,
      endSeconds,
      startRatio,
      widthRatio: Math.min(
        1 - startRatio,
        Math.max(MIN_SEGMENT_RATIO, (endSeconds - startSeconds) / span)
      ),
    });
    row.talkSeconds += endSeconds - startSeconds;
    bySpeaker.set(speaker, row);
  }

  if (bySpeaker.size < 2) return null;
  return { rows: [...bySpeaker.values()], durationSeconds: span, ticks: buildAxisTicks(span) };
}

/** `0:00`, the round steps that fit, then the recording's own end. */
function buildAxisTicks(span: number): SpeakerActivityTick[] {
  const widest = AXIS_STEPS_SECONDS[AXIS_STEPS_SECONDS.length - 1] as number;
  const step = AXIS_STEPS_SECONDS.find((candidate) => span / candidate <= MAX_AXIS_TICKS) ?? widest;
  const ticks: SpeakerActivityTick[] = [];
  for (let seconds = 0; seconds < span; seconds += step) {
    // The closing tick is the recording's end, so a step landing almost on it would print two
    // labels a few seconds apart on top of each other.
    if (seconds > 0 && span - seconds < step / 2) break;
    ticks.push({ seconds, label: formatSpeakerActivityTime(seconds), ratio: seconds / span });
  }
  ticks.push({ seconds: span, label: formatSpeakerActivityTime(span), ratio: 1 });
  return ticks;
}

/**
 * A point or a length on the timeline, in the same `m:ss` / `h:mm:ss` the dialog's own header
 * states the recording's length in.
 */
export function formatSpeakerActivityTime(seconds: number): string {
  return formatDuration(Math.max(0, seconds) * 1_000, { style: "media" });
}

/** `27:39 – 31:08`, the range one segment covers. */
export function formatSpeakerActivityRange(segment: SpeakerActivitySegment): string {
  return `${formatSpeakerActivityTime(segment.startSeconds)} – ${formatSpeakerActivityTime(segment.endSeconds)}`;
}
